// R2 — audio/video upload via a Cloudflare Worker that issues short-lived
// presigned PUT URLs. The browser POSTs metadata to the Worker, receives a
// PUT URL, then PUTs the file directly to R2. R2 secrets stay in Worker
// secrets and never reach the client.

// The Worker URL is not a secret — secrets live inside the Worker itself.
// Fall back to the hardcoded URL so a missing/wrong Railway env var never
// silently routes uploads to the wrong endpoint.
const presignUrl =
  process.env.REACT_APP_R2_PRESIGN_URL ||
  'https://gia-pha-r2-presign.gia-pha.workers.dev';

export const r2Configured = !!presignUrl;

export async function uploadAV(file, kind) {
  if (!r2Configured) throw new Error('R2 presign URL not configured');
  if (kind !== 'audio' && kind !== 'video') {
    throw new Error(`Invalid R2 upload kind: ${kind}`);
  }

  const presignRes = await fetch(presignUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind,
      filename: file.name || `${kind}-${Date.now()}`,
      content_type: file.type || 'application/octet-stream',
    }),
  });
  if (!presignRes.ok) {
    const text = await presignRes.text().catch(() => '');
    throw new Error(`R2 presign failed: ${presignRes.status} ${text}`);
  }
  const { put_url, public_url, key } = await presignRes.json();

  const putRes = await fetch(put_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw new Error(`R2 PUT failed: ${putRes.status} ${text}`);
  }
  return { url: public_url, key };
}

// Best-effort delete from R2 via the Worker. Accepts either an explicit key
// (preferred — captured at upload time) or a public URL (parsed for the
// /audio/<file> or /video/<file> path segment for items uploaded before
// keys were stored).
export async function deleteAV(keyOrUrl) {
  if (!r2Configured) return { ok: false, reason: 'not_configured' };
  const key = deriveR2Key(keyOrUrl);
  if (!key) return { ok: false, reason: 'no_key' };
  try {
    const res = await fetch(
      `${presignUrl}?key=${encodeURIComponent(key)}`,
      { method: 'DELETE' }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.warn('[gia-pha] R2 delete failed:', res.status, text);
      return { ok: false, reason: 'delete_failed', status: res.status };
    }
    return { ok: true };
  } catch (e) {
    console.warn('[gia-pha] R2 delete threw:', e);
    return { ok: false, reason: 'network_error' };
  }
}

export function deriveR2Key(keyOrUrl) {
  if (typeof keyOrUrl !== 'string' || !keyOrUrl) return null;
  // Already a bare key.
  if (/^(audio|video)\/[A-Za-z0-9._-]+$/.test(keyOrUrl)) return keyOrUrl;
  // Public URL — pull out the audio|video path segment.
  const m = keyOrUrl.match(/\/((?:audio|video)\/[A-Za-z0-9._-]+)(?:[?#]|$)/);
  return m ? m[1] : null;
}
